import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Button } from '../common/Button';
import * as api from '../../api/user-app-api';

const mapContainerStyle = {
    width: '100%',
    height: '300px',
    borderRadius: '12px',
    marginBottom: '1rem',
};

export const MyAddressScreen = ({ setView, params }) => {
    const [addresses, setAddresses] = useState([]);
    const [profileName, setProfileName] = useState("");
    const [editingAddress, setEditingAddress] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 23.0225, lng: 72.5714 }); // Default: Ahmedabad
    const [markerPosition, setMarkerPosition] = useState(mapCenter);

    const userId = localStorage.getItem("userId");

    // ✅ Load Google Maps
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: "", // ⚠️ Replace with your actual key
    });

    useEffect(() => {
        console.log("MyAddress params:", params);

        fetchAddresses();
        fetchProfileName();
    }, []);

    const fetchAddresses = async () => {
        setIsLoading(true);
        try {
            const res = await api.fetchUserAddresses({ user_id: userId });
            if (res.data.status === "success") {
                setAddresses(res.data.data);
            } else {
                setAddresses([]);
            }
        } catch (err) {
            console.error("Error loading addresses:", err);
            setError("Failed to load addresses.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchProfileName = async () => {
        try {
            const res = await api.fetchUserProfile({ id: userId });
            if (res.data.status === "success") {
                setProfileName(res.data.profile.name || "");
            }
        } catch (err) {
            console.error("Error loading profile name:", err);
        }
    };


    // 📍 Get current location and update map
    const handleUseCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    const { latitude, longitude } = pos.coords;
                    const newPosition = { lat: latitude, lng: longitude };
                    setMapCenter(newPosition);
                    setMarkerPosition(newPosition);
                    await reverseGeocode(latitude, longitude);
                },
                (err) => {
                    console.error(err);
                    alert("Unable to access location. Please enable GPS and try again.");
                }
            );
        } else {
            alert("Geolocation is not supported by your browser.");
        }
    };

    // 🌍 Reverse geocode function
    const reverseGeocode = async (lat, lng) => {
        const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=YOUR_GOOGLE_MAPS_API_KEY`;
        const response = await fetch(geocodeUrl);
        const data = await response.json();

        if (data.results?.[0]) {
            const address = data.results[0].formatted_address;
            const city =
                data.results[0].address_components.find(c => c.types.includes('locality'))?.long_name || '';
            const postal =
                data.results[0].address_components.find(c => c.types.includes('postal_code'))?.long_name || '';

            // Fill form inputs automatically
            document.querySelector("input[name='line1']").value = address;
            document.querySelector("input[name='city']").value = city;
            document.querySelector("input[name='pincode']").value = postal;
        }
    };

    const onMarkerDragEnd = useCallback(async (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setMarkerPosition({ lat, lng });
        await reverseGeocode(lat, lng);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsSaving(true);

        const formData = new FormData(e.target);
        const nameFromForm = formData.get("name");

        // 👇 Update user name in USERS table (same like Profile page)
        try {
            await api.updateUserProfile({
                id: userId,
                name: nameFromForm
            });
        } catch (err) {
            console.error("Error updating user name:", err);
        }
        const payload = {
            user_id: userId,
            name: nameFromForm,
            house_no: formData.get("house_no"),
            building_name: formData.get("building_name"),
            road_1: formData.get("road_1"),
            road_2: formData.get("road_2"),
            landmark: formData.get("landmark"),
            pincode: formData.get("pincode"),
            city: formData.get("city"),
            state: formData.get("state"),
            latitude: markerPosition.lat,
            longitude: markerPosition.lng,
            is_default: addresses.length === 0 ? 1 : 0,
        };


        try {
            if (editingAddress && editingAddress.id) {
                payload.id = editingAddress.id;
                await api.updateAddress(payload);
            } else {
                await api.addAddress(payload);
            }

            setEditingAddress(null);

            // 👇 IMPORTANT — wait for fresh addresses
            const res = await api.fetchUserAddresses({ user_id: userId });
            const list = res.data?.data || [];
            console.log("Updated address list:", list, params?.returnTo);
            if (params?.returnTo === "cart") {
                // pass new address also (optional but safer)
                setView("cart", {
                    goToStep: 3,
                    newAddress: list[list.length - 1] || null
                });
            } else {
                setView("Profile");
            }

        } catch (err) {
            console.error("Error saving address:", err);
            setError("Failed to save address. Please try again.");
        } finally {
            setIsSaving(false);
        }


    };

    if (isLoading) return <div className="p-4">Loading addresses...</div>;

    if (editingAddress || addresses.length === 0) {
        return (
<div className="relative h-[calc(100vh-80px)] overflow-y-scroll scrollbar-thin scrollbar-custom pb-24">

                <div className="p-4 pt-0 mb-16">
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">

                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <input
                                type="text"
                                name="name"
                                required
                                defaultValue={editingAddress?.name || profileName}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                            />
                        </div>

                        {/* House No */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">House No.</label>
                            <input
                                type="text"
                                name="house_no"
                                required
                                defaultValue={editingAddress?.house_no || ""}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                            />
                        </div>

                        {/* Building Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">House / Building Name</label>
                            <input
                                type="text"
                                name="building_name"
                                required
                                defaultValue={editingAddress?.building_name || ""}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                            />
                        </div>

                        {/* Road 1 + Road 2 */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Road No. 1</label>
                                <input
                                    type="text"
                                    name="road_1"
                                    required
                                    defaultValue={editingAddress?.road_1 || ""}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Road No. 2</label>
                                <input
                                    type="text"
                                    name="road_2"
                                    // required
                                    defaultValue={editingAddress?.road_2 || ""}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                                />
                            </div>
                        </div>

                        {/* Landmark */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Area / Landmark</label>
                            <input
                                type="text"
                                name="landmark"
                                required
                                defaultValue={editingAddress?.landmark || ""}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                            />
                        </div>

                        {/* Pincode + City */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                                <input
                                    type="text"
                                    name="pincode"
                                    required
                                    pattern="[0-9]{6}"
                                    defaultValue={editingAddress?.pincode || ""}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                <input
                                    type="text"
                                    name="city"
                                    required
                                    defaultValue={editingAddress?.city || ""}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                                />
                            </div>
                        </div>

                        {/* State */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <input
                                type="text"
                                name="state"
                                required
                                defaultValue={editingAddress?.state || ""}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-[#01a499]"
                            />
                        </div>

                        {error && <div className="p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>}

                        <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t pb-7 flex space-x-2">
                            <Button
                                type="button"
                                onClick={() => setEditingAddress(null)}
                                className="!bg-gray-500 w-1/3"
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSaving} className="w-2/3">
                                {isSaving ? "Saving..." : "Save Address"}
                            </Button>
                        </div>
                    </form>


                </div>

            </div>
        );
    }

    return (
        <div className="relative h-screen flex flex-col">
            <div className="flex-1 overflow-y-auto p-4 pt-0 pb-28">
                <div className="flex justify-between items-center mb-6">
                    <div></div>
                    {addresses.length === 0 && (
                        <button
                            onClick={() => setEditingAddress({})}
                            className="text-sm text-[#01a499] font-medium"
                        >
                            + Add New
                        </button>
                    )}
                </div>

                <div className="space-y-4">
                    {addresses.map(address => (
                        <div
                            key={address.id}
                            className="bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                        >
                            <div className="flex justify-between items-start">

                                {/* LEFT SIDE */}
                                <div>
                                    <p className="font-medium text-gray-800">
                                        {address.house_no}, {address.building_name}
                                        {address.is_default === 1 && (
                                            <span className="ml-2 text-xs text-[#01a499] font-medium">
                                                Default
                                            </span>
                                        )}
                                    </p>

                                    <p className="text-sm text-gray-700 mt-1">
                                        Road 1: {address.road_1}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Road 2: {address.road_2}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Landmark: {address.landmark}
                                    </p>

                                    <p className="text-sm text-gray-700 mt-2">
                                        {address.city}, {address.state} - {address.pincode}
                                    </p>

                                </div>

                                {/* RIGHT SIDE */}
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setEditingAddress(address)}
                                        className="p-2 text-blue-600"
                                    >
                                        Edit
                                    </button>
                                </div>

                            </div>
                        </div>
                    ))}
                </div>


                {params?.returnTo && (
                    <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t">
                        <Button onClick={() => setView(params.returnTo)}>Continue</Button>
                    </div>
                )}
            </div>
        </div>
    );
};
